import { useEffect, useCallback } from "react";
import { useURLState } from "./useURLState";
import { CREATIVE_ROUTE_CONFIG } from "../config/routeConfig";

/**
 * Route validation hook for Creative system (flat 4-tab model)
 * Auto-fixes invalid routes with smart fallbacks
 */
export const useRouteValidation = (options = {}) => {
  const {
    autoRedirect = true,
    onInvalidRoute = null
  } = options;

  const { urlState, updateURL } = useURLState();

  const isRouteValid = useCallback(() => {
    const { tab, pageNumber } = urlState;
    return CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) && pageNumber > 0;
  }, [urlState]);

  const getValidationErrors = useCallback(() => {
    const { tab, pageNumber } = urlState;
    const errors = [];
    if (!CREATIVE_ROUTE_CONFIG.validTabs.includes(tab)) {
      errors.push({ parameter: 'tab', value: tab });
    }
    if (pageNumber < 1) {
      errors.push({ parameter: 'pageNumber', value: pageNumber });
    }
    return errors;
  }, [urlState]);

  const fixInvalidRoute = useCallback(() => {
    const { tab, pageNumber } = urlState;

    // If tab is valid but page number is bad, fix just the page
    if (CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) && pageNumber < 1) {
      updateURL({ tab, page: 1 });
      return;
    }

    // Full redirect to default
    if (autoRedirect) {
      updateURL({ tab: CREATIVE_ROUTE_CONFIG.defaultRoute.tab, page: 1 });
      if (onInvalidRoute) {
        onInvalidRoute(getValidationErrors(), urlState);
      }
    }
  }, [urlState, updateURL, autoRedirect, onInvalidRoute, getValidationErrors]);

  useEffect(() => {
    if (!isRouteValid()) {
      fixInvalidRoute();
    }
  }, [isRouteValid, fixInvalidRoute]);

  const validateRoute = useCallback(() => {
    const valid = isRouteValid();
    if (!valid) fixInvalidRoute();
    return valid;
  }, [isRouteValid, fixInvalidRoute]);

  const isParameterCombinationValid = useCallback((tab, _subtab, pageNumber = 1) => {
    return CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) && pageNumber > 0;
  }, []);

  return {
    isRouteValid,
    validateRoute,
    getValidationErrors,
    isParameterCombinationValid,
    currentState: urlState
  };
};

export default useRouteValidation;
